/**
 * Kolectra Linguistic Taxonomy — the 16 failure labels.
 *
 * When the model is defeated on a segment (a FAIL verdict → the segment ships
 * as a CHALLENGE SET), the human reviewer annotates *why* it failed: which
 * language phenomenon tripped it up. The label id (1–16) is what we persist and
 * send to the server.
 *
 * See ANNOTATOR_LABEL spec. The Judgement UI surfaces these in a select that
 * appears only when a verdict is FAIL.
 */

export interface FailureLabel {
  /** Stable id sent to the server (1–16). */
  id: number;
  /** Short label for the select option. */
  label: string;
}

export const FAILURE_LABELS: readonly FailureLabel[] = [
  { id: 1, label: 'non-standard or regional variety' },
  { id: 2, label: 'one word, many meanings' },
  { id: 3, label: 'figurative/idiomatic expression' },
  { id: 4, label: 'unclear who does/says it' },
  { id: 5, label: "unclear who it's done to / for" },
  { id: 6, label: 'complex word or sentence structure' },
  { id: 7, label: 'sarcasm / hidden meaning' },
  { id: 8, label: 'polite/indirect refusal or avoidance' },
  { id: 9, label: 'question assumes something false' },
  { id: 10, label: 'requires cultural knowledge' },
  { id: 11, label: 'meaning depends on stress/intonation/delivery' },
  { id: 12, label: 'mixing languages' },
  { id: 13, label: 'model uses wrong formality/politeness level' },
  { id: 14, label: 'false start / incomplete speech' },
  { id: 15, label: 'abbreviation / slang term not recognized' },
  { id: 16, label: 'translation correct but reasoning wrong' },
] as const;

const BY_ID = new Map(FAILURE_LABELS.map((l) => [l.id, l]));

/** Look up a failure label by its id. */
export const failureLabel = (id: number): FailureLabel | undefined => BY_ID.get(id);

/** Whether an id is a valid failure-label id. */
export const isValidFailureLabel = (id: number): boolean => BY_ID.has(id);
